import React, { useState, useCallback } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { LocationAutocomplete, type LocationData } from '@/components/ui/LocationAutocomplete/LocationAutocomplete';
import { Alert } from '@/components/ui/Alert/Alert';
import { Badge } from '@/components/ui/Badge/Badge';
import { Input } from '@/components/ui/Input';
import {
  AlertCircle,
  MapPin,
  X,
  Settings2,
  CircleDot,
  Building2,
  Check,
  Loader2,
  Hotel,
  Database,
  Sparkles,
  Globe,
  Search,
  PenLine,
  Map,
  Layers,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  googlePlacesSearchService,
  type HotelSearchResult,
  type ExistingVenueInfo
} from '@/services/GooglePlacesSearchService';
import { VenuesService } from '@/services/VenuesService';
import { ResearchService } from '@/services/ResearchService';
import type { DestinationSearchMode, RegionSource, CreateRegionInput } from '@/services/ResearchService';
import { RegionCitySelector, type SelectedCity } from './RegionCitySelector';

/**
 * Preview status for a pending destination
 */
interface PreviewStatus {
  status: 'idle' | 'loading' | 'loaded' | 'error';
  hotels: HotelSearchResult[];
  existing_count: number;
  new_count: number;
  existing_venues: Record<string, ExistingVenueInfo>;
  error?: string;
}

/**
 * Extended destination data with search settings and preview
 */
interface PendingDestination {
  city: string;
  country: string;
  region?: string;
  place_id?: string;
  formatted_address: string;
  location_json?: any;
  lat: number;
  lon: number;
  isCountry: boolean;
  // Search settings
  search_mode: DestinationSearchMode;
  search_radius_km: number;
  // Settings confirmed (locked in)
  settingsConfirmed: boolean;
  // Preview data
  preview: PreviewStatus;
}

interface AddDestinationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (destination: {
    city: string;
    country: string;
    region?: string;
    place_id?: string;
    location_json?: any;
    search_mode?: DestinationSearchMode;
    search_radius_km?: number;
  }) => Promise<void>;
  onAddRegion?: (input: CreateRegionInput) => Promise<void>;
  eventId: string;
  currentCount: number;
  existingDestinations?: Array<{ city: string; country: string }>;
}

type AddMode = 'city' | 'region';
type RegionTab = 'search' | 'custom' | 'polygon';

export function AddDestinationModal({
  isOpen,
  onClose,
  onAdd,
  onAddRegion,
  eventId,
  currentCount,
  existingDestinations = []
}: AddDestinationModalProps) {
  // Mode: city or region
  const [addMode, setAddMode] = useState<AddMode>('city');

  // Region-specific state
  const [regionTab, setRegionTab] = useState<RegionTab>('search');
  const [regionSearchValue, setRegionSearchValue] = useState('');
  const [regionName, setRegionName] = useState('');
  const [regionCountry, setRegionCountry] = useState('');
  const [regionSource, setRegionSource] = useState<RegionSource>('google_places');
  const [regionPlaceId, setRegionPlaceId] = useState<string | undefined>();
  const [regionLocationJson, setRegionLocationJson] = useState<any>(undefined);
  const [selectedCities, setSelectedCities] = useState<SelectedCity[]>([]);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveryError, setDiscoveryError] = useState<string | null>(null);
  const [isCreatingRegion, setIsCreatingRegion] = useState(false);
  const [regionStep, setRegionStep] = useState<'name' | 'cities'>('name');

  const researchService = new ResearchService();

  // City-specific state
  const [searchValue, setSearchValue] = useState('');
  const [pendingDestinations, setPendingDestinations] = useState<PendingDestination[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [triggerAiResearch, setTriggerAiResearch] = useState(true);

  // Currently selected location (before settings are confirmed)
  const [currentLocation, setCurrentLocation] = useState<{
    city: string;
    country: string;
    region?: string;
    place_id?: string;
    formatted_address: string;
    location_json: any;
    lat: number;
    lon: number;
    isCountry: boolean;
  } | null>(null);
  
  // Settings for current location
  const [currentSearchMode, setCurrentSearchMode] = useState<DestinationSearchMode>('city_name');
  const [currentRadiusKm, setCurrentRadiusKm] = useState(15);

  const venuesService = new VenuesService();

  function handleClose() {
    // Reset city state
    setSearchValue('');
    setPendingDestinations([]);
    setCurrentLocation(null);
    setCurrentSearchMode('city_name');
    setCurrentRadiusKm(15);
    // Reset region state
    setAddMode('city');
    setRegionTab('search');
    setRegionSearchValue('');
    setRegionName('');
    setRegionCountry('');
    setRegionSource('google_places');
    setRegionPlaceId(undefined);
    setRegionLocationJson(undefined);
    setSelectedCities([]);
    setIsDiscovering(false);
    setDiscoveryError(null);
    setRegionStep('name');
    setError(null);
    onClose();
  }

  // Region: trigger LLM city discovery and geocode immediately
  async function discoverCities(name: string, country: string) {
    setIsDiscovering(true);
    setDiscoveryError(null);
    // Move to cities step immediately so RegionCitySelector can show the loader
    setRegionStep('cities');
    try {
      const cities = await researchService.discoverCities(name, country);
      
      // Geocode each discovered city immediately to get standardized data
      // This ensures city names match the format used when adding cities directly
      const geocodedCities = await Promise.all(
        cities.map(async (c) => {
          try {
            const geocoded = await googlePlacesSearchService.geocodeCity(c.city, c.country);
            if (geocoded) {
              // Standardize city name to match handleLocationSelect logic:
              // Use geocoded.city if available, otherwise extract from formatted_address
              // This ensures consistency with direct city addition via LocationAutocomplete
              let standardizedCity = geocoded.city;
              if (!standardizedCity || standardizedCity.includes(',')) {
                // If city name contains comma or is missing, extract from formatted_address
                // This handles cases like "55100 Siena, Province of Siena, Italy"
                standardizedCity = geocoded.formatted_address?.split(',')[0]?.trim() || c.city;
              }
              
              return {
                city: standardizedCity,
                country: geocoded.country || c.country,
                selected: true,
                source: 'llm' as const,
                relevance: c.relevance,
                has_hotels: c.has_hotels,
                place_id: geocoded.place_id,
                location_json: geocoded.location_json,
              };
            }
          } catch (geoError: any) {
            // If geocoding fails, keep the city but log the error
            console.warn(`Failed to geocode ${c.city}, ${c.country}:`, geoError);
          }
          
          // Fallback: use original city data without geocoding
          return {
            ...c,
            selected: true,
            source: 'llm' as const,
            place_id: undefined,
            location_json: undefined,
          };
        })
      );
      
      setSelectedCities(geocodedCities);
    } catch (err: any) {
      setDiscoveryError(err.message || 'Failed to discover cities');
    } finally {
      setIsDiscovering(false);
    }
  }

  // Region: handle Google region selection
  function handleRegionLocationSelect(location: LocationData) {
    const name = location.region || location.city || location.formatted_address.split(',')[0].trim();
    const country = location.country || 'Unknown';
    setRegionName(name);
    setRegionCountry(country);
    setRegionSource('google_places');
    setRegionPlaceId(location.place_id);
    setRegionLocationJson({
      formatted_address: location.formatted_address,
      place_id: location.place_id,
      geometry: {
        location: { lat: location.lat, lng: location.lon },
      },
    });
    setRegionSearchValue('');
    setError(null);
    // Auto-trigger discovery
    discoverCities(name, country);
  }

  // Region: handle custom name confirmation
  function handleCustomRegionConfirm() {
    if (!regionName.trim() || !regionCountry.trim()) {
      setError('Please enter both a region name and country');
      return;
    }
    setRegionSource('custom');
    setRegionPlaceId(undefined);
    setRegionLocationJson(undefined);
    setError(null);
    // Auto-trigger discovery
    discoverCities(regionName.trim(), regionCountry.trim());
  }

  // Region: create region with selected cities
  async function handleCreateRegion() {
    const citiesToAdd = selectedCities.filter(c => c.selected);
    if (citiesToAdd.length === 0) {
      setError('Please select at least one city');
      return;
    }

    if (!onAddRegion) {
      setError('Region creation not supported');
      return;
    }

    setIsCreatingRegion(true);
    setError(null);

    try {
      // Geocode cities that don't have location_json (e.g., from LLM discovery)
      const geocodingErrors: string[] = [];
      const geocodedCities = await Promise.all(
        citiesToAdd.map(async (c) => {
          // Already has location data
          if (c.location_json && c.place_id) {
            return {
              city: c.city,
              country: c.country,
              place_id: c.place_id,
              location_json: c.location_json,
            };
          }

          // Try to geocode (fallback for cities that weren't geocoded during discovery)
          try {
            const geocoded = await googlePlacesSearchService.geocodeCity(c.city, c.country);
            if (geocoded) {
              // Standardize city name to match handleLocationSelect logic
              let standardizedCity = geocoded.city;
              if (!standardizedCity || standardizedCity.includes(',')) {
                // Extract from formatted_address if city name contains comma or is missing
                standardizedCity = geocoded.formatted_address?.split(',')[0]?.trim() || c.city;
              }
              
              return {
                city: standardizedCity,
                country: c.country,
                place_id: geocoded.place_id,
                location_json: geocoded.location_json,
              };
            }
          } catch (geoError: any) {
            // Extract detailed error message
            const errorMessage = geoError?.message || geoError?.toString() || 'Unknown error';
            const detailedError = `Failed to geocode ${c.city}, ${c.country}: ${errorMessage}`;
            geocodingErrors.push(detailedError);
            console.warn(detailedError, geoError);
          }

          // Fallback: create without geolocation (can be added via Edit later)
          return {
            city: c.city,
            country: c.country,
            place_id: undefined,
            location_json: undefined,
          };
        })
      );

      // Show geocoding errors as warnings, but don't block region creation
      if (geocodingErrors.length > 0) {
        const errorSummary = geocodingErrors.length === 1 
          ? geocodingErrors[0]
          : `${geocodingErrors.length} cities failed to geocode:\n${geocodingErrors.join('\n')}`;
        console.warn('Geocoding warnings:', errorSummary);
        // Note: We continue anyway - cities without geocoding can be added manually later
      }

      await onAddRegion({
        display_name: regionName,
        country: regionCountry,
        region_source: regionSource,
        place_id: regionPlaceId,
        location_json: regionLocationJson,
        cities: geocodedCities,
      });
      handleClose();
    } catch (err: any) {
      // Extract detailed error message
      let errorMessage = 'Failed to create region';
      if (err?.message) {
        errorMessage = err.message;
      } else if (err?.error?.message) {
        errorMessage = err.error.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err?.toString) {
        errorMessage = err.toString();
      }
      
      // If we have geocoding errors, include them in the error message
      setError(errorMessage);
      console.error('Region creation error:', err);
    } finally {
      setIsCreatingRegion(false);
    }
  }

  function handleLocationSelect(location: LocationData) {
    const isCountry = location.location_type === 'country';
    
    // Determine the primary name and country
    let cityName: string;
    let countryName: string;
    
    if (isCountry) {
      cityName = location.country || location.formatted_address.split(',')[0].trim();
      countryName = cityName;
    } else {
      cityName = location.city || location.formatted_address.split(',')[0].trim();
      countryName = location.country || 'Unknown';
    }

    if (!cityName || !countryName) {
      setError('Invalid location selected. Please try again.');
      return;
    }

    // Check if destination already exists in database
    const existsInDb = existingDestinations.some(
      (d) => d.city.toLowerCase() === cityName.toLowerCase() && 
             d.country.toLowerCase() === countryName.toLowerCase()
    );

    if (existsInDb) {
      setError('This destination is already added to your event');
      return;
    }

    // Check if destination already pending
    const existsInPending = pendingDestinations.some(
      (d) => d.city.toLowerCase() === cityName.toLowerCase() && 
             d.country.toLowerCase() === countryName.toLowerCase()
    );

    if (existsInPending) {
      setError(`This ${isCountry ? 'country' : 'city'} has already been added`);
      return;
    }

    // Set as current location
    setCurrentLocation({
      city: cityName,
      country: countryName,
      region: location.region || undefined,
      place_id: location.place_id,
      formatted_address: location.formatted_address,
      location_json: {
        formatted_address: location.formatted_address,
        place_id: location.place_id,
        geometry: {
          location: {
            lat: location.lat,
            lng: location.lon,
          },
        },
      },
      lat: location.lat,
      lon: location.lon,
      isCountry,
    });

    // Reset search mode to default
    setCurrentSearchMode('city_name');
    setCurrentRadiusKm(15);
    setSearchValue('');
    setError(null);
  }

  const confirmSettingsAndPreview = useCallback(async () => {
    if (!currentLocation) return;

    // Create the pending destination with settings
    const newDestination: PendingDestination = {
      ...currentLocation,
      search_mode: currentLocation.isCountry ? 'city_name' : currentSearchMode,
      search_radius_km: currentSearchMode === 'radius' ? currentRadiusKm : 0,
      settingsConfirmed: true,
      preview: {
        status: 'loading',
        hotels: [],
        existing_count: 0,
        new_count: 0,
        existing_venues: {},
      },
    };

    // Add to pending list immediately (with loading state)
    setPendingDestinations(prev => [...prev, newDestination]);
    setCurrentLocation(null);

    // Determine search mode for Google Places API
    const searchMode = newDestination.search_mode === 'radius' ? 'radius' : 'text';

    try {
      // Call preview API
      const result = await googlePlacesSearchService.previewImport({
        location: {
          lat: newDestination.lat,
          lon: newDestination.lon,
          name: newDestination.formatted_address,
        },
        search_mode: searchMode,
        radius_km: newDestination.search_radius_km || 15,
      });

      // Update the destination with preview results
      setPendingDestinations(prev => prev.map(d => 
        d.place_id === newDestination.place_id && d.city === newDestination.city
          ? {
              ...d,
              preview: {
                status: 'loaded' as const,
                hotels: result.hotels,
                existing_count: result.existing_count,
                new_count: result.new_count,
                existing_venues: result.existing_venues,
              },
            }
          : d
      ));
    } catch (err: any) {
      // Update with error
      setPendingDestinations(prev => prev.map(d => 
        d.place_id === newDestination.place_id && d.city === newDestination.city
          ? {
              ...d,
              preview: {
                status: 'error' as const,
                hotels: [],
                existing_count: 0,
                new_count: 0,
                existing_venues: {},
                error: err.message || 'Failed to search Google Places',
              },
            }
          : d
      ));
    }
  }, [currentLocation, currentSearchMode, currentRadiusKm]);

  function removeDestination(index: number) {
    setPendingDestinations(prev => prev.filter((_, i) => i !== index));
    setError(null);
  }

  function cancelCurrentSelection() {
    setCurrentLocation(null);
    setCurrentSearchMode('city_name');
    setCurrentRadiusKm(15);
    setError(null);
  }

  async function handleAddAll() {
    if (pendingDestinations.length === 0) {
      setError('Please add at least one destination');
      return;
    }

    // Check if any destinations are still loading
    const stillLoading = pendingDestinations.some(d => d.preview.status === 'loading');
    if (stillLoading) {
      setError('Please wait for all destination previews to complete');
      return;
    }

    try {
      setIsAdding(true);
      setError(null);

      // Process each destination
      for (const destination of pendingDestinations) {
        // 1. Import new hotels from preview
        if (destination.preview.status === 'loaded' && destination.preview.new_count > 0) {
          const hotelsToImport = destination.preview.hotels.filter(
            h => !destination.preview.existing_venues[h.placeId]
          );

          if (hotelsToImport.length > 0) {
            const importResult = await googlePlacesSearchService.importHotels(
              hotelsToImport.map(h => ({
                placeId: h.placeId,
                name: h.name,
                address: h.address,
                lat: h.lat,
                lon: h.lon,
                rating: h.rating,
                userRatingCount: h.userRatingCount,
                phone: h.phone,
                website: h.website,
              }))
            );

            // 2. Trigger AI research if enabled and we have imported venues
            if (triggerAiResearch && importResult.importedIds.length > 0) {
              try {
                await venuesService.bulkAction('enrich', {
                  allFiltered: false,
                  ids: importResult.importedIds,
                });
              } catch (researchError) {
                console.error('AI research trigger failed:', researchError);
                // Don't fail the whole operation if research fails
              }
            }
          }
        }

        // 3. Create the destination
        await onAdd({
          city: destination.city,
          country: destination.country,
          region: destination.region,
          place_id: destination.place_id,
          location_json: destination.location_json,
          search_mode: destination.search_mode,
          search_radius_km: destination.search_mode === 'radius' ? destination.search_radius_km : undefined,
        });
      }

      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add destinations');
    } finally {
      setIsAdding(false);
    }
  }

  // Calculate totals for display
  const totalNewVenues = pendingDestinations
    .filter(d => d.preview.status === 'loaded')
    .reduce((sum, d) => sum + d.preview.new_count, 0);
  const totalExistingVenues = pendingDestinations
    .filter(d => d.preview.status === 'loaded')
    .reduce((sum, d) => sum + d.preview.existing_count, 0);
  const anyLoading = pendingDestinations.some(d => d.preview.status === 'loading');

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      title="Add Destinations"
      description="Add destinations and configure how venues are discovered"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            {addMode === 'city' && pendingDestinations.length > 0 && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={triggerAiResearch}
                  onChange={(e) => setTriggerAiResearch(e.target.checked)}
                  className="w-4 h-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
                  disabled={isAdding}
                />
                <Sparkles className="w-4 h-4 text-primary-600" />
                <span className="text-sm text-gray-700">Trigger AI Research</span>
              </label>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={handleClose} disabled={isAdding || isCreatingRegion}>
              Cancel
            </Button>
            {addMode === 'city' ? (
              <Button
                variant="primary"
                onClick={handleAddAll}
                disabled={pendingDestinations.length === 0 || isAdding || anyLoading}
                isLoading={isAdding}
              >
                {anyLoading ? 'Searching...' : (
                  <>
                    Add {pendingDestinations.length > 0 ? `${pendingDestinations.length} ` : ''}
                    Destination{pendingDestinations.length !== 1 ? 's' : ''}
                    {totalNewVenues > 0 && ` (Import ${totalNewVenues} venues)`}
                  </>
                )}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleCreateRegion}
                disabled={
                  regionStep !== 'cities' ||
                  selectedCities.filter(c => c.selected).length === 0 ||
                  isCreatingRegion
                }
                isLoading={isCreatingRegion}
                leftIcon={<Layers className="w-4 h-4" />}
              >
                Create Region ({selectedCities.filter(c => c.selected).length} cities)
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Mode Toggle: Add City | Add Region */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => { setAddMode('city'); setError(null); }}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors',
              addMode === 'city'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            <Building2 className="w-4 h-4" />
            Add City
          </button>
          <button
            type="button"
            onClick={() => { setAddMode('region'); setError(null); }}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors',
              addMode === 'region'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            <Layers className="w-4 h-4" />
            Add Region
          </button>
        </div>

        {/* ============================================ */}
        {/* REGION MODE */}
        {/* ============================================ */}
        {addMode === 'region' && (
          <div className="space-y-4">
            {/* Global loader for region discovery - shows when discovering cities */}
            {isDiscovering && regionName && (
              <div className="flex items-center gap-3 p-4 bg-primary-50 rounded-lg border border-primary-200">
                <Loader2 className="w-5 h-5 text-primary-600 animate-spin flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-primary-900">
                    Discovering cities in {regionName}...
                  </p>
                  <p className="text-xs text-primary-600 mt-0.5">
                    Using AI to find relevant destinations
                  </p>
                </div>
              </div>
            )}
            
            {/* Region Tab Bar */}
            {regionStep === 'name' && (
              <>
                <div className="flex border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => { setRegionTab('search'); setError(null); }}
                    className={cn(
                      'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                      regionTab === 'search'
                        ? 'border-primary-600 text-primary-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    )}
                  >
                    <Search className="w-3.5 h-3.5" />
                    Search Name
                  </button>
                  <button
                    type="button"
                    onClick={() => { setRegionTab('custom'); setError(null); }}
                    className={cn(
                      'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                      regionTab === 'custom'
                        ? 'border-primary-600 text-primary-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    )}
                  >
                    <PenLine className="w-3.5 h-3.5" />
                    Custom Name
                  </button>
                  <button
                    type="button"
                    disabled
                    className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-gray-300 cursor-not-allowed"
                    title="Coming in Phase 2"
                  >
                    <Map className="w-3.5 h-3.5" />
                    Draw on Map
                    <Badge variant="secondary" className="text-xs ml-1">Soon</Badge>
                  </button>
                </div>

                {/* Tab 1: Search by Name */}
                {regionTab === 'search' && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">
                      Search for a region, state, or province using Google Places.
                    </p>
                    <LocationAutocomplete
                      value={regionSearchValue}
                      onChange={setRegionSearchValue}
                      onLocationSelect={handleRegionLocationSelect}
                      placeholder="Search for a region (e.g., Tuscany, Provence...)"
                      types="regions"
                      disabled={isDiscovering}
                    />
                    {/* Show loader when discovering cities after region selection */}
                    {isDiscovering && regionName && (
                      <div className="flex items-center gap-2 p-4 bg-primary-50 rounded-lg border border-primary-200">
                        <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-primary-900">
                            Discovering cities in {regionName}...
                          </p>
                          <p className="text-xs text-primary-600 mt-0.5">
                            Using AI to find relevant destinations
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Tab 2: Custom Name */}
                {regionTab === 'custom' && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">
                      Create a region with any name — no Google lookup required.
                    </p>
                    <div className="space-y-3">
                      <Input
                        value={regionName}
                        onChange={(e) => setRegionName(e.target.value)}
                        placeholder="Region name (e.g., Croatian Islands, Lake District)"
                        label="Region Name"
                      />
                      <Input
                        value={regionCountry}
                        onChange={(e) => setRegionCountry(e.target.value)}
                        placeholder="Country (e.g., Croatia, United Kingdom)"
                        label="Country"
                      />
                      <Button
                        variant="primary"
                        onClick={handleCustomRegionConfirm}
                        disabled={!regionName.trim() || !regionCountry.trim() || isDiscovering}
                        className="w-full"
                      >
                        Discover Cities
                      </Button>
                    </div>
                    {/* Show loader when discovering cities after custom name confirmation */}
                    {isDiscovering && regionName && (
                      <div className="flex items-center gap-2 p-4 bg-primary-50 rounded-lg border border-primary-200">
                        <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-primary-900">
                            Discovering cities in {regionName}...
                          </p>
                          <p className="text-xs text-primary-600 mt-0.5">
                            Using AI to find relevant destinations
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Region step 2: City selection (after name is established) */}
            {regionStep === 'cities' && (
              <div className="space-y-4">
                {/* Region header */}
                <div className="flex items-center justify-between p-3 bg-primary-50 rounded-lg border border-primary-200">
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary-600" />
                    <div>
                      <div className="font-medium text-primary-900">{regionName}</div>
                      <div className="text-sm text-primary-600">{regionCountry}</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setRegionStep('name');
                      setSelectedCities([]);
                      setRegionName('');
                      setRegionCountry('');
                    }}
                    className="text-primary-400 hover:text-primary-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* City selector */}
                <RegionCitySelector
                  regionName={regionName}
                  country={regionCountry}
                  discoveredCities={[]}
                  isDiscovering={isDiscovering}
                  discoveryError={discoveryError}
                  selectedCities={selectedCities}
                  onCitiesChange={setSelectedCities}
                  onRetryDiscovery={() => discoverCities(regionName, regionCountry)}
                />
              </div>
            )}
          </div>
        )}

        {/* ============================================ */}
        {/* CITY MODE (existing behavior) */}
        {/* ============================================ */}
        {addMode === 'city' && (<>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
              1
            </div>
            <span className="text-sm font-medium text-gray-700">Select Location</span>
          </div>

          <LocationAutocomplete
            value={searchValue}
            onChange={setSearchValue}
            onLocationSelect={handleLocationSelect}
            placeholder="Search for a city or country..."
            types="cities"
            disabled={isAdding || currentLocation !== null}
          />
        </div>

        {/* Step 2: Configure Settings (for current selection) */}
        {currentLocation && (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                  2
                </div>
                <span className="text-sm font-medium text-gray-700">Configure Search Settings</span>
              </div>
              <button
                type="button"
                onClick={cancelCurrentSelection}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Selected Location Display */}
            <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-gray-200">
              <MapPin className="w-5 h-5 text-primary-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {currentLocation.formatted_address}
                </div>
                <div className="text-sm text-gray-500">
                  {currentLocation.lat.toFixed(4)}, {currentLocation.lon.toFixed(4)}
                </div>
              </div>
              {currentLocation.isCountry && (
                <Badge variant="secondary" className="flex-shrink-0">Country</Badge>
              )}
            </div>

            {/* Search Mode Settings (only for cities, not countries) */}
            {!currentLocation.isCountry && (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Search Mode</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setCurrentSearchMode('city_name')}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left',
                        currentSearchMode === 'city_name'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      )}
                    >
                      <Building2 className={cn(
                        'w-5 h-5 flex-shrink-0',
                        currentSearchMode === 'city_name' ? 'text-primary-600' : 'text-gray-400'
                      )} />
                      <div>
                        <div className={cn(
                          'font-medium text-sm',
                          currentSearchMode === 'city_name' ? 'text-primary-900' : 'text-gray-700'
                        )}>
                          City Name
                        </div>
                        <div className="text-xs text-gray-500">
                          Match venues by city field
                        </div>
                      </div>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setCurrentSearchMode('radius')}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left',
                        currentSearchMode === 'radius'
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      )}
                    >
                      <CircleDot className={cn(
                        'w-5 h-5 flex-shrink-0',
                        currentSearchMode === 'radius' ? 'text-primary-600' : 'text-gray-400'
                      )} />
                      <div>
                        <div className={cn(
                          'font-medium text-sm',
                          currentSearchMode === 'radius' ? 'text-primary-900' : 'text-gray-700'
                        )}>
                          Radius Search
                        </div>
                        <div className="text-xs text-gray-500">
                          Include nearby areas
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Radius Slider (only when radius mode is selected) */}
                {currentSearchMode === 'radius' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Search Radius</span>
                      <span className="font-medium text-gray-900">{currentRadiusKm} km</span>
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={50}
                      step={1}
                      value={currentRadiusKm}
                      onChange={(e) => setCurrentRadiusKm(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>1 km</span>
                      <span>50 km</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Countries get city_name search automatically */}
            {currentLocation.isCountry && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Building2 className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-blue-800">
                  Country destinations use name-based matching (radius search not available)
                </span>
              </div>
            )}

            {/* Confirm Settings Button */}
            <Button
              variant="primary"
              onClick={confirmSettingsAndPreview}
              className="w-full"
              leftIcon={<Settings2 className="w-4 h-4" />}
            >
              Confirm & Search Hotels
            </Button>
          </div>
        )}

        {/* Pending Destinations List */}
        {pendingDestinations.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Destinations to Add ({pendingDestinations.length})
              </span>
              {(totalExistingVenues > 0 || totalNewVenues > 0) && (
                <div className="flex items-center gap-2">
                  {totalExistingVenues > 0 && (
                    <Badge variant="secondary">
                      <Database className="w-3 h-3 mr-1" />
                      {totalExistingVenues} in database
                    </Badge>
                  )}
                  {totalNewVenues > 0 && (
                    <Badge variant="success">
                      <Hotel className="w-3 h-3 mr-1" />
                      {totalNewVenues} new
                    </Badge>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {pendingDestinations.map((destination, idx) => (
                <DestinationCard
                  key={`${destination.place_id}-${idx}`}
                  destination={destination}
                  onRemove={() => removeDestination(idx)}
                  disabled={isAdding}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State (city mode only) */}
        {pendingDestinations.length === 0 && !currentLocation && (
          <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
            <p className="text-sm text-primary-900">
              <strong>Smart Venue Discovery:</strong> Search for a city or country, configure how
              venues are searched, and we'll automatically find hotels from Google Places and import
              any new ones into your database.
            </p>
          </div>
        )}
        {/* End of city mode */}
        </>)}

        {/* Error Alert (shared) */}
        {error && (
          <Alert variant="error">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </Alert>
        )}
      </div>
    </Modal>
  );
}

/**
 * Individual destination card with preview status
 */
function DestinationCard({ 
  destination, 
  onRemove,
  disabled 
}: { 
  destination: PendingDestination; 
  onRemove: () => void;
  disabled: boolean;
}) {
  const { preview } = destination;

  return (
    <div className={cn(
      'p-3 rounded-lg border',
      preview.status === 'loading' ? 'bg-gray-50 border-gray-200' :
      preview.status === 'error' ? 'bg-red-50 border-red-200' :
      preview.status === 'loaded' ? 'bg-white border-gray-200' :
      'bg-gray-50 border-gray-200'
    )}>
      <div className="flex items-start gap-3">
        {/* Status Icon */}
        <div className="flex-shrink-0 pt-0.5">
          {preview.status === 'loading' ? (
            <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
          ) : preview.status === 'error' ? (
            <AlertCircle className="w-5 h-5 text-red-500" />
          ) : (
            <Check className="w-5 h-5 text-green-500" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate">
              {destination.formatted_address}
            </span>
            <Badge variant="secondary" className="flex-shrink-0 text-xs">
              {destination.search_mode === 'radius' 
                ? `${destination.search_radius_km}km radius` 
                : 'City name'}
            </Badge>
          </div>

          {/* Preview Results */}
          {preview.status === 'loading' && (
            <p className="text-sm text-gray-500 mt-1">Searching Google Places...</p>
          )}
          {preview.status === 'error' && (
            <p className="text-sm text-red-600 mt-1">{preview.error}</p>
          )}
          {preview.status === 'loaded' && (
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-gray-500">
                Found {preview.hotels.length} hotels
              </span>
              {preview.existing_count > 0 && (
                <span className="text-sm text-gray-500">
                  • {preview.existing_count} already in database
                </span>
              )}
              {preview.new_count > 0 && (
                <span className="text-sm text-green-600 font-medium">
                  • {preview.new_count} new to import
                </span>
              )}
            </div>
          )}
        </div>

        {/* Remove Button */}
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
